import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Check localStorage on mount (persists across refresh)
    return localStorage.getItem('isAuthenticated') === 'true';
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isEmulating, setIsEmulating] = useState(() => {
    return localStorage.getItem('isEmulating') === 'true';
  });

  useEffect(() => {
    // Ensure auth state is loaded from localStorage before rendering protected routes
    const authState = localStorage.getItem('isAuthenticated') === 'true';
    const emulatingState = localStorage.getItem('isEmulating') === 'true';
    setIsAuthenticated(authState || emulatingState);
    setIsEmulating(emulatingState);
    setIsLoading(false);
  }, []);

  const login = () => {
    setIsAuthenticated(true);
    localStorage.setItem('isAuthenticated', 'true');
  };
  
  const logout = () => {
    setIsAuthenticated(false);
    setIsEmulating(false);
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('currentEvent');
    // Clear emulation data
    localStorage.removeItem('isEmulating');
    localStorage.removeItem('emulatedUserEmail');
    localStorage.removeItem('emulatedUserName');
    localStorage.removeItem('emulatedUserPartnerId');
    localStorage.removeItem('emulatedUserIsEventManager');
    sessionStorage.removeItem('emulatedUser');
  };

  const exitEmulation = () => {
    // Clear emulation data
    localStorage.removeItem('isEmulating');
    localStorage.removeItem('emulatedUserEmail');
    localStorage.removeItem('emulatedUserName');
    localStorage.removeItem('emulatedUserPartnerId');
    localStorage.removeItem('emulatedUserIsEventManager');
    sessionStorage.removeItem('emulatedUser');
    setIsEmulating(false);
    // Redirect to superadmin
    window.location.href = '/superadmin';
  };

  const getEmulatedUser = () => {
    if (!isEmulating) return null;
    return {
      email: localStorage.getItem('emulatedUserEmail'),
      name: localStorage.getItem('emulatedUserName'),
      partner_id: localStorage.getItem('emulatedUserPartnerId'),
      is_event_manager: localStorage.getItem('emulatedUserIsEventManager') === 'true'
    };
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      isLoading, 
      login, 
      logout,
      isEmulating,
      exitEmulation,
      getEmulatedUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};
